import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { contactSchema } from "./contact-validation";

export const sendContactMessage = createServerFn({ method: "POST" })
  .validator((input: unknown) => {
    const parsed = contactSchema.safeParse(input);
    return { parsed };
  })
  .handler(async ({ data }) => {
    if (!data.parsed.success) {
      return {
        ok: false as const,
        reason: "invalid" as const,
        message: "Please fill in your name, a valid email and a message of at least 10 characters.",
      };
    }
    const input = data.parsed.data;


    const forwardedFor = getRequestHeader("x-forwarded-for") ?? "";
    const clientIp = forwardedFor.split(",")[0]?.trim() || getRequestHeader("cf-connecting-ip") || "";

    const { guardForm, hashIp } = await import("./form-guard.server");
    const verdict = guardForm({
      origin: getRequestHeader("origin"),
      referer: getRequestHeader("referer"),
      honeypot: input.honeypot,
      startedAt: input.startedAt,
      minFillMs: 3_000,
      text: `${input.name}\n${input.company}\n${input.message}`,
      keys: [
        ...(clientIp ? [{ key: `contact:${hashIp(clientIp)}`, max: 3 }] : []),
        { key: `contact:mail:${input.email.toLowerCase()}`, max: 3 },
      ],
    });
    if (!verdict.ok) {
      console.warn("[contact] blocked:", verdict.kind);
      const { recordGuardBlock, identifyRequestUser } = await import("./guard-log.server");
      const actor = await identifyRequestUser(getRequestHeader("authorization"));
      await recordGuardBlock({
        source: "contact",
        kind: verdict.kind,
        ip: clientIp,
        path: getRequestHeader("referer") ?? null,
        detail: input.formId ?? null,
        userId: actor.userId,
        userEmail: actor.email,
      });
      if (verdict.kind === "honeypot") {
        // Silently accept so bots do not learn anything.
        return { ok: true as const, confirmationSent: false };
      }
      if (verdict.kind === "rate_limited") {
        return {
          ok: false as const,
          reason: "rate_limited" as const,
          message:
            "You have sent several messages already. Please wait a few minutes before sending another one.",
        };
      }
      return {
        ok: false as const,
        reason: "spam" as const,
        message:
          verdict.kind === "timing"
            ? "Please take a moment to fill in the form, then send it again."
            : "Your message was blocked by our spam filter. Please email info@oxory.net directly.",
      };
    }

    const { sendGmail } = await import("./email.server");
    const { createContactTicket, markTicketEmailStatus } = await import("./tickets.server");

    // Store the enquiry first so nothing is lost if email delivery fails.
    const ticketRef = await createContactTicket({
      name: input.name,
      company: input.company,
      email: input.email,
      topic: input.topic,
      message: input.message,
      formId: input.formId,
    });


    const body = [
      "New enquiry via the contact form on oxory.net",
      "",
      `Ticket: ${ticketRef ?? "not stored"}`,
      `Name: ${input.name}`,
      `Company: ${input.company || "-"}`,
      `Email: ${input.email}`,
      `Topic: ${input.topic}`,
      "",
      input.message,
    ].join("\n");

    const subject = `[oxory.net]${ticketRef ? ` [${ticketRef}]` : ""} ${input.topic} — ${input.name}`;

    try {
      await sendGmail({
        to: "info@oxory.net",
        subject,
        body,
        replyTo: input.email,
      });

      // Additional internal copy — failures here must not break the enquiry.
      try {
        await sendGmail({
          to: "oxory.asds@gmail.com",
          subject,
          body,
          replyTo: input.email,
        });
      } catch (copyError) {
        console.error("[contact] copy to oxory.asds@gmail.com failed:", copyError);
      }
    } catch (error) {
      console.error("[contact] Gmail send failed:", error);
      if (ticketRef) {
        await markTicketEmailStatus(
          ticketRef,
          "failed",
          error instanceof Error ? error.message : String(error),
        );
      }
      return {
        ok: false as const,
        reason: "send_failed" as const,
        ticketRef,
        message: ticketRef
          ? `We saved your enquiry (reference ${ticketRef}) but could not send the email. Please email info@oxory.net directly.`
          : "We could not deliver your message. Please email info@oxory.net directly.",
      };
    }

    // Confirmation copy for the sender — failures here must not break the enquiry.
    let confirmationSent = false;
    try {
      // Only fixed text + server-generated reference: caller-supplied content is never
      // echoed to the (unverified) submitted address, so the form cannot relay messages.
      const copy = [
        "Hello,",
        "",
        "thank you for contacting OXORY. We have received your message and will get back to you shortly.",
        "",
        ...(ticketRef ? [`Reference: ${ticketRef}`, ""] : []),
        "If you did not submit this enquiry, you can ignore this email.",
        "",
        "--",
        "OXORY — IT & Technology Consulting | AMS Services | Freelance & Partner Placement",
        "info@oxory.net · https://oxory.net",
      ].join("\n");

      await sendGmail({
        to: input.email,
        subject: `Your enquiry to OXORY${ticketRef ? ` (${ticketRef})` : ""}`,
        body: copy,
        replyTo: "info@oxory.net",
      });
      confirmationSent = true;
    } catch (error) {
      console.error("[contact] confirmation copy failed:", error);
    }

    if (ticketRef) await markTicketEmailStatus(ticketRef, "sent");

    return { ok: true as const, ticketRef, confirmationSent };
  });


